<script setup lang="ts">
    import defaultImage from '@/assets/images/no-image.jpg';
    import { buildPreviewLink } from '@/utils/buildPreviewLink';

    type ModifiedDocumentSearchHit = Omit<SearchHit, '_source'> & {
        _source: AggregatedDocument;
    };

    type Result = SearchHit | ModifiedDocumentSearchHit;

    const props = defineProps<{ result: Result }>();

    const { t } = useI18n();
    const { lang, urlSegment } = useLanguage();
    const hasSearched = inject<Ref<boolean>>('hasSearched');

    const onImageError = (event: Event) => {
        (event.target as HTMLImageElement).src = defaultImage;
    };

    const imageId = computed(() => props.result._source.medice_main_picture_id || null);
    const name = computed(
        () =>
            props.result._source.censhare_name?.[lang.value] ||
            props.result._source.censhare_name?.default ||
            props.result._source.medice_product_brand ||
            props.result._source.storyblok_title?.[lang.value]?.[0]
    );

    const filterTypeTranslationKey = computed(() => {
        let s = 'searchPreview.filterType.';

        if (props.result._source.storyblok_section === 'news') {
            s += 'news';
        } else if (props.result._source.storyblok_section === 'fachkreis') {
            s += 'area';
        } else {
            s += props.result._index;
        }

        return s;
    });

    const elasticsearchHighlightCount = computed(() => {
        if (props.result._index !== 'storyblok') return -1;
        return props.result?.highlight?.[`storyblok_body.${lang.value}`]?.length || 0;
    });

    const sendHasSearched = () => {
        hasSearched!.value = true;
    };
</script>

<template>
    <NuxtLink
        class="preview-card"
        :onclick="sendHasSearched"
        :href="buildPreviewLink(props.result, urlSegment, lang)"
    >
        <div class="image-container">
            <img
                v-if="props.result._index === 'document'"
                src="~/assets/icons/file-down.svg"
                alt=""
                aria-hidden="true"
            />

            <CenshareImage
                v-else-if="props.result._index === 'product' && imageId"
                :image-id="imageId"
                :name="props.result._source.censhare_name?.[lang!] || `Product Image`"
                type="productThumbnail"
            />

            <img
                v-else-if="props.result._source.storyblok_section === 'news'"
                src="~/assets/icons/newspaper.svg"
                alt=""
                aria-hidden="true"
            />

            <img
                v-else-if="props.result._source.storyblok_section === 'fachkreis'"
                src="~/assets/icons/file-text.svg"
                alt=""
                aria-hidden="true"
            />

            <img
                v-else
                :src="defaultImage"
                alt="no-image"
                @error="onImageError"
            />
        </div>

        <div class="text-content">
            <span class="type">
                {{ t(filterTypeTranslationKey) }}
            </span>

            <p class="name">
                {{ name }}
            </p>

            <span
                v-if="props.result._index === 'storyblok' && elasticsearchHighlightCount > 0"
                class="hits"
            >
                {{ t('search.documentHits') }} {{ elasticsearchHighlightCount }}
            </span>

            <span
                v-else-if="props.result._index === 'document' && 'totalChunks' in props.result._source"
                class="hits"
            >
                {{ t('search.documentHits') }} {{ props.result._source.totalChunks }}
            </span>
        </div>
    </NuxtLink>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/search/search-preview-result.scss';
</style>
